import { useEffect, useId, useRef } from 'react';
import clsx from 'clsx';
import Icon from './Icon.jsx';
import { haptic } from '../app/settingsStore.js';
import styles from './Field.module.scss';

export function Field({ label, hint, error, children, className }) {
  const id = useId();
  const child = typeof children === 'function' ? children(id) : children;
  return (
    <div className={clsx(styles.field, className)}>
      {label ? (
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
      ) : null}
      {child}
      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className={styles.hint}>{hint}</p>
      ) : null}
    </div>
  );
}

export function TextInput({ label, hint, error, className, ...rest }) {
  return (
    <Field label={label} hint={hint} error={error} className={className}>
      {(id) => <input id={id} className={clsx(styles.input, error && styles.invalid)} {...rest} />}
    </Field>
  );
}

export function Select({ label, hint, error, className, children, ...rest }) {
  return (
    <Field label={label} hint={hint} error={error} className={className}>
      {(id) => (
        <select id={id} className={clsx(styles.input, styles.select, error && styles.invalid)} {...rest}>
          {children}
        </select>
      )}
    </Field>
  );
}

export function TextArea({ label, hint, className, ...rest }) {
  return (
    <Field label={label} hint={hint} className={className}>
      {(id) => <textarea id={id} className={clsx(styles.input, styles.textarea)} {...rest} />}
    </Field>
  );
}

/** Small set of mutually exclusive options shown side by side. */
export function Segmented({ label, value, onChange, options, className }) {
  return (
    <Field label={label} className={className}>
      {() => (
        <div className={styles.segmented} role="radiogroup" aria-label={label}>
          {options.map((o) => (
            <button
              type="button"
              key={o.value}
              role="radio"
              aria-checked={value === o.value}
              aria-label={o.ariaLabel}
              className={clsx(styles.segment, value === o.value && styles.segmentOn)}
              onClick={() => {
                haptic(5);
                onChange(o.value);
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </Field>
  );
}

/** A whole row you can tap; the switch is just the visual. */
export function Toggle({ label, hint, checked, onChange, icon }) {
  return (
    <label className={styles.toggleRow}>
      {icon ? (
        <span className={styles.rowIcon}>
          <Icon name={icon} size={20} />
        </span>
      ) : null}
      <span className={styles.toggleText}>
        <span className={styles.toggleLabel}>{label}</span>
        {hint ? <span className={styles.rowHint}>{hint}</span> : null}
      </span>
      <input
        type="checkbox"
        role="switch"
        className={styles.switch}
        checked={!!checked}
        onChange={(e) => {
          haptic(5);
          onChange(e.target.checked);
        }}
      />
    </label>
  );
}

/**
 * Options as tappable cards (replaces dropdowns). `layout`: 'list' | 'grid' | 'scroll'.
 * Each option: { value, label, description?, icon?, preview? }.
 */
export function ChoiceCards({ label, hint, value, onChange, options, layout = 'list', className, size }) {
  return (
    <Field label={label} hint={hint} className={className}>
      {() => (
        <div
          className={clsx(styles.choices, styles[`choices_${layout}`], size === 'sm' && styles.choicesSm)}
          role="radiogroup"
          aria-label={label}
        >
          {options.map((o) => {
            const on = value === o.value;
            return (
              <button
                key={String(o.value)}
                type="button"
                role="radio"
                aria-checked={on}
                className={clsx(styles.choice, on && styles.choiceOn, o.preview && styles.choiceStacked)}
                onClick={() => {
                  haptic(5);
                  onChange(o.value);
                }}
              >
                {o.preview ? <span className={styles.choicePreview}>{o.preview}</span> : null}
                {o.icon ? (
                  <span className={styles.choiceIcon}>
                    <Icon name={o.icon} size={22} />
                  </span>
                ) : null}
                <span className={styles.choiceText}>
                  <span className={styles.choiceLabel}>{o.label}</span>
                  {o.description ? <span className={styles.choiceDesc}>{o.description}</span> : null}
                </span>
                <span className={styles.choiceCheck} aria-hidden="true">
                  <Icon name="check" size={16} stroke={3} />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </Field>
  );
}

/** Compact pill choices. `multiple` makes it a set of toggles. */
export function Chips({ label, value, onChange, options, multiple = false, scroll = false, className }) {
  const isOn = (v) => (multiple ? (value ?? []).includes(v) : value === v);
  const press = (v) => {
    haptic(5);
    if (!multiple) return onChange(v);
    const set = new Set(value ?? []);
    if (set.has(v)) set.delete(v);
    else set.add(v);
    return onChange([...set]);
  };
  const body = (
    <div
      className={clsx(styles.chips, scroll && styles.chipsScroll)}
      role={multiple ? 'group' : 'radiogroup'}
      aria-label={label}
    >
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          role={multiple ? 'checkbox' : 'radio'}
          aria-checked={isOn(o.value)}
          className={clsx(styles.chip, isOn(o.value) && styles.chipOn)}
          onClick={() => press(o.value)}
        >
          {o.icon ? <Icon name={o.icon} size={16} /> : null}
          {o.label}
        </button>
      ))}
    </div>
  );
  if (!label) return <div className={className}>{body}</div>;
  return (
    <Field label={label} className={className}>
      {() => body}
    </Field>
  );
}

function useHoldRepeat(onStep) {
  const timer = useRef(null);
  const latest = useRef(onStep);
  useEffect(() => {
    latest.current = onStep;
  });
  const fire = () => latest.current();
  const stop = () => {
    clearTimeout(timer.current);
    clearInterval(timer.current);
    timer.current = null;
  };
  useEffect(() => stop, []);
  return {
    onPointerDown: (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      e.preventDefault();
      fire();
      timer.current = setTimeout(() => {
        timer.current = setInterval(fire, 90);
      }, 420);
    },
    onPointerUp: stop,
    onPointerLeave: stop,
    onPointerCancel: stop,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        fire();
      }
    },
  };
}

/** Number input with big − / + buttons (press and hold to repeat). */
export function Stepper({ label, hint, value, onChange, min = 0, max = 9999, step = 1, suffix }) {
  const id = useId();
  const n = Number(value) || 0;
  const clamp = (v) => Math.min(max, Math.max(min, v));
  const stepBy = (dir) => {
    const next = clamp(Math.round((n + dir * step) / step) * step);
    if (next !== n) {
      haptic(4);
      onChange(next);
    }
  };
  const dec = useHoldRepeat(() => stepBy(-1));
  const inc = useHoldRepeat(() => stepBy(1));
  return (
    <div className={styles.stepperRow}>
      <span className={styles.toggleText}>
        <label htmlFor={id} className={styles.toggleLabel}>
          {label}
        </label>
        {hint ? <span className={styles.rowHint}>{hint}</span> : null}
      </span>
      <div className={styles.stepper}>
        <button type="button" className={styles.stepBtn} aria-label={`Decrease ${label}`} disabled={n <= min} {...dec}>
          <Icon name="minus" size={20} stroke={2.5} />
        </button>
        <span className={styles.stepValue}>
          <input
            id={id}
            inputMode="numeric"
            pattern="[0-9]*"
            value={value}
            onChange={(e) => onChange(e.target.value.replace(/[^\d]/g, ''))}
            onBlur={() => onChange(clamp(n))}
            onFocus={(e) => e.target.select()}
          />
          {suffix ? <small>{suffix}</small> : null}
        </span>
        <button type="button" className={styles.stepBtn} aria-label={`Increase ${label}`} disabled={n >= max} {...inc}>
          <Icon name="plus" size={20} stroke={2.5} />
        </button>
      </div>
    </div>
  );
}

/** A tappable row that shows the current value and opens a picker or page. */
export function PickerRow({ label, value, hint, icon, onClick, placeholder = 'Choose', className, ...rest }) {
  return (
    <button type="button" className={clsx(styles.pickerRow, className)} onClick={onClick} {...rest}>
      {icon ? (
        <span className={styles.rowIcon}>
          <Icon name={icon} size={20} />
        </span>
      ) : null}
      <span className={styles.toggleText}>
        <span className={styles.pickerLabel}>{label}</span>
        <span className={clsx(styles.pickerValue, !value && styles.pickerEmpty)}>{value || placeholder}</span>
        {hint ? <span className={styles.rowHint}>{hint}</span> : null}
      </span>
      <Icon name="chevron" size={18} className={styles.chev} />
    </button>
  );
}

/** Grouped container for rows (Settings-style). */
export function Group({ title, footer, children, className }) {
  return (
    <section className={clsx(styles.group, className)}>
      {title ? <h2 className={styles.groupTitle}>{title}</h2> : null}
      <div className={styles.groupBody}>{children}</div>
      {footer ? <p className={styles.groupFooter}>{footer}</p> : null}
    </section>
  );
}
